import React, { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { mockUsers } from '../../mockData/portalData';
import { listUsers, createUser, updateUserRoles, addUserToCampus, removeUserFromCampus, addTeachingCampus, removeTeachingCampus, disableUser, enableUser, deleteUser, getRoleDisplayName, VISIBLE_PORTAL_ROLES, ROLE_DISPLAY_NAMES, getLinkedStudents, updateParentStudents } from '../../api/portalUserApi';
import { listCampuses, getCampusName } from '../../api/campusPortalApi';
import { listStudents } from '../../api/studentPortalApi';
import { getUserProfile, updateUserProfile, uploadUserAvatar, getMyProfile } from '../../api/userProfileApi';
import { listSubjectsCached } from '../../api/subjectPortalApi';
import { YEAR_OPTIONS } from '../../api/courseGroupPortalApi';
import AvatarUpload from '../../components/Portal/AvatarUpload';
import StudentMultiSelect from '../../components/Portal/StudentMultiSelect';
import * as S from './Management.styles';
import { ATAR_SUBJECTS, teachesAtarYears, keepAtarSubjectsForGrades } from '../../utils/atarSubjects';

const UserManagement = () => {
  const auth = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterCampus, setFilterCampus] = useState('all');

  // Get current user's subId from Cognito
  const currentUserSub = auth.user?.profile?.sub || '';

  // Get user's Cognito groups
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const portalGroups = userGroups.filter(g => g.startsWith('portal_'));

  // Permission checks based on Cognito groups
  const isDeveloper = portalGroups.includes('portal_developer');
  const isSystemAdmin = portalGroups.includes('portal_system_admin');
  const isDirector = portalGroups.includes('portal_director');
  const isManager = portalGroups.includes('portal_manager');
  const isAdmin = portalGroups.includes('portal_admin');
  const canViewUsers = isDeveloper || isSystemAdmin || isDirector || isManager || isAdmin;
  const canCreateUser = isDeveloper || isSystemAdmin || isDirector || isManager || isAdmin;
  const canEditCampus = isDeveloper || isSystemAdmin;

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCampusModal, setShowCampusModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserCampusId, setNewUserCampusId] = useState('');
  const [newUserRole, setNewUserRole] = useState('');           // Single role for creation
  const [newUserPhone, setNewUserPhone] = useState('');         // Phone for staff roles
  const [selectedStudents, setSelectedStudents] = useState([]); // Student IDs for parent role
  const [availableStudents, setAvailableStudents] = useState([]); // Students in selected campus
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [userCampusIds, setUserCampusIds] = useState([]);  // Multi-campus: current user's campus list (management)
  const [campusToAdd, setCampusToAdd] = useState('');      // Multi-campus: campus to add
  const [teachingCampusIds, setTeachingCampusIds] = useState([]); // Teaching campuses (no management authority)
  const [teachingCampusToAdd, setTeachingCampusToAdd] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  // Edit Roles - parent student selection
  const [editRoleStudents, setEditRoleStudents] = useState([]);
  const [editRoleAvailableStudents, setEditRoleAvailableStudents] = useState([]);
  const [editRoleLoadingStudents, setEditRoleLoadingStudents] = useState(false);
  // Linked Students management modal
  const [showLinkedStudentsModal, setShowLinkedStudentsModal] = useState(false);
  const [linkedStudentsAvailable, setLinkedStudentsAvailable] = useState([]);
  const [linkedStudentsSelected, setLinkedStudentsSelected] = useState([]);
  const [linkedStudentsLoading, setLinkedStudentsLoading] = useState(false);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    birthday: '',
    address: '',
    education: '',
    wwcNumber: '',
    bio: '',
    hireDate: '',
    department: '',
    position: '',
    avatarUrl: '',
    teachableGrades: [],
    teachableSubjectIds: []
  });
  const [profileSubjects, setProfileSubjects] = useState([]);

  // Confirmation modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'disable' | 'delete'
  const [confirmUser, setConfirmUser] = useState(null);

  // Campus data
  const [campuses, setCampuses] = useState([]);
  const [myOwnCampusIds, setMyOwnCampusIds] = useState([]); // Director/Manager/Admin's own campuses for restricted editing

  // Fetch users on mount (auth is handled by PortalLayout)
  useEffect(() => {
    if (!canViewUsers) {
      // If user doesn't have permission, show limited view
      setLoading(false);
      return;
    }

    fetchUsers();
  }, [canViewUsers]);

  // Fetch campuses for the dropdown
  useEffect(() => {
    const fetchCampuses = async () => {
      try {
        const campusList = await listCampuses();
        setCampuses(campusList);
      } catch (err) {
        console.error('Error fetching campuses:', err);
      }
    };
    fetchCampuses();
  }, []);

  // Fetch admin's/director's own campuses for restricted editing
  useEffect(() => {
    const fetchMyOwnCampuses = async () => {
      if ((isAdmin || isDirector || isManager) && !canEditCampus) {
        try {
          const profile = await getMyProfile();
          const myCampuses = profile?.campusIds || (profile?.campusId ? [profile.campusId] : []);
          if (myCampuses.length > 0) {
            setMyOwnCampusIds(myCampuses);
          }
        } catch (err) {
          console.error('Error fetching own campuses:', err);
        }
      }
    };
    fetchMyOwnCampuses();
  }, [isAdmin, isDirector, isManager, canEditCampus]);

  // Fetch students when parent role is selected and campus is chosen
  useEffect(() => {
    const fetchStudentsForParent = async () => {
      if (newUserRole === 'portal_parent' && newUserCampusId) {
        setLoadingStudents(true);
        try {
          const students = await listStudents({ campusId: newUserCampusId });
          // Only show students without a parent assigned
          const unassignedStudents = students.filter(s => !s.parentId);
          setAvailableStudents(unassignedStudents);
        } catch (err) {
          console.error('Error fetching students:', err);
          setAvailableStudents([]);
        } finally {
          setLoadingStudents(false);
        }
      } else {
        setAvailableStudents([]);
      }
    };
    fetchStudentsForParent();
  }, [newUserRole, newUserCampusId]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedUsers = await listUsers();
      setUsers(fetchedUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
      // Fallback to mock data for demo
      setUsers(mockUsers.map(u => ({
        ...u,
        roles: [u.role === 'admin' ? 'portal_director' : `portal_${u.role}`]
      })));
    } finally {
      setLoading(false);
    }
  };

  // Helper: Get visible roles (excludes system_admin)
  const getVisibleRoles = (roles) => {
    return (roles || []).filter(r => r !== 'portal_system_admin' && r !== 'portal_developer');
  };

  const filteredUsers = users.filter(user => {
    // Hide users who only have portal_system_admin role
    const visibleRoles = getVisibleRoles(user.roles);
    if (visibleRoles.length === 0 && (user.roles || []).length > 0) {
      return false;
    }

    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    // Role filter
    const matchesRole = filterRole === 'all' || visibleRoles.includes(filterRole);

    // Campus filter (only for System Admin, others see filtered list from backend)
    let matchesCampus = true;
    if (canEditCampus && filterCampus !== 'all') {
      const userCampuses = user.campusIds || (user.campusId ? [user.campusId] : []);
      if (filterCampus === 'unassigned') {
        matchesCampus = userCampuses.length === 0;
      } else {
        matchesCampus = userCampuses.includes(filterCampus);
      }
    }

    return matchesSearch && matchesRole && matchesCampus;
  });

  const getRoleColor = (role) => {
    if (role === 'portal_developer') return '#0ea5e9';
    if (role === 'portal_system_admin') return '#dc2626';
    if (role === 'portal_director') return '#f59e0b';
    if (role === 'portal_manager') return '#8b5cf6';
    if (role === 'portal_admin') return '#a855f7';
    if (role === 'portal_teacher') return '#10b981';
    if (role === 'portal_parent') return '#3b82f6';
    return '#6b7280';
  };

  // Close create modal and reset all form fields
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setNewUserEmail('');
    setNewUserName('');
    setNewUserCampusId('');
    setNewUserRole('');
    setNewUserPhone('');
    setSelectedStudents([]);
    setAvailableStudents([]);
  };

  const handleCreateUser = async () => {
    if (!canSubmitCreateUser()) {
      alert('Please fill in all required fields / 请填写所有必填字段');
      return;
    }

    setActionLoading(true);
    try {
      // Build profile data for staff roles
      const profile = isStaffRole(newUserRole) ? { phone: newUserPhone } : {};

      // Build student IDs for parent role
      const studentIds = isParentRole(newUserRole) ? selectedStudents : [];

      const newUser = await createUser(
        newUserEmail,
        newUserName,
        newUserCampusId,
        newUserRole,
        profile,
        studentIds
      );

      setUsers([...users, newUser]);
      handleCloseCreateModal();
      alert('User created successfully! A temporary password has been sent to their email.\n\n用户创建成功！临时密码已发送至邮箱。');
    } catch (err) {
      console.error('Error creating user:', err);
      alert(`Failed to create user: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditRoles = (user) => {
    setSelectedUser(user);
    setSelectedRoles(user.roles || []);
    setEditRoleStudents([]);
    setEditRoleAvailableStudents([]);
    setShowEditModal(true);
  };

  const handleSaveRoles = async () => {
    if (!selectedUser) return;

    const wasParent = (selectedUser.roles || []).includes('portal_parent');
    const isNowParent = selectedRoles.includes('portal_parent');
    const addingParent = isNowParent && !wasParent;

    if (addingParent && editRoleStudents.length === 0) {
      alert('Please select at least one student for the parent role.\n请为家长角色选择至少一名学生。');
      return;
    }

    setActionLoading(true);
    try {
      await updateUserRoles(selectedUser.id, selectedRoles, addingParent ? editRoleStudents : undefined);
      setUsers(users.map(u =>
        u.id === selectedUser.id ? { ...u, roles: selectedRoles } : u
      ));
      setShowEditModal(false);
      setSelectedUser(null);
      setEditRoleStudents([]);
      setEditRoleAvailableStudents([]);
      alert('User roles updated successfully!');
    } catch (err) {
      console.error('Error updating roles:', err);
      alert(`Failed to update roles: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Multi-role toggle - allows users to have multiple roles
  const toggleRole = async (role) => {
    if (selectedRoles.includes(role)) {
      setSelectedRoles(selectedRoles.filter(r => r !== role));
      if (role === 'portal_parent') {
        setEditRoleStudents([]);
        setEditRoleAvailableStudents([]);
      }
    } else {
      setSelectedRoles([...selectedRoles, role]);
      // When adding parent role, fetch available students from user's campuses
      if (role === 'portal_parent' && !(selectedUser?.roles || []).includes('portal_parent')) {
        const userCampuses = selectedUser?.campusIds || (selectedUser?.campusId ? [selectedUser.campusId] : []);
        if (userCampuses.length > 0) {
          setEditRoleLoadingStudents(true);
          try {
            const allStudents = await listStudents();
            const campusStudents = allStudents.filter(s =>
              (s.campuses || []).some(c => userCampuses.includes(c))
            );
            setEditRoleAvailableStudents(campusStudents.filter(s => !s.parentId));
          } catch (err) {
            console.error('Error fetching students for parent role:', err);
            setEditRoleAvailableStudents([]);
          } finally {
            setEditRoleLoadingStudents(false);
          }
        } else {
          setEditRoleAvailableStudents([]);
        }
      }
    }
  };

  // Permission check for managing user status
  // Admin can disable/enable but NOT delete
  const canDisableUser = isDeveloper || isSystemAdmin || isDirector || isManager || isAdmin;
  const canDeleteUser = isDeveloper || isSystemAdmin || isDirector || isManager;

  // Get roles that current user can assign to others
  const getAssignableRoles = () => {
    // Developer can assign all visible roles
    if (isDeveloper) {
      return VISIBLE_PORTAL_ROLES;
    }
    // System Admin can assign all roles except system_admin
    if (isSystemAdmin) {
      return VISIBLE_PORTAL_ROLES;
    }
    // Director can assign manager, admin, teacher, parent (not director)
    if (isDirector) {
      return VISIBLE_PORTAL_ROLES.filter(r => r !== 'portal_director');
    }
    // Manager can assign admin, teacher, parent (not director or manager)
    if (isManager) {
      return VISIBLE_PORTAL_ROLES.filter(r =>
        r !== 'portal_director' && r !== 'portal_manager'
      );
    }
    // Admin can only assign teacher, parent
    if (isAdmin) {
      return ['portal_teacher', 'portal_parent'];
    }
    return [];
  };

  // Get available campuses for user creation (manager/admin restricted to own campuses)
  const getAvailableCampuses = () => {
    if (isDeveloper || isSystemAdmin) {
      return campuses;
    }
    // For director/manager/admin, find their own campuses (support multi-campus)
    const currentUserData = users.find(u => u.subId === currentUserSub);
    const userCampuses = currentUserData?.campusIds || (currentUserData?.campusId ? [currentUserData.campusId] : []);
    if (userCampuses.length > 0) {
      return campuses.filter(c => userCampuses.includes(c.id));
    }
    return [];
  };

  // Check if role is staff (manager/admin/teacher)
  const isStaffRole = (role) =>
    ['portal_manager', 'portal_admin', 'portal_teacher'].includes(role);

  // Check if role is parent
  const isParentRole = (role) => role === 'portal_parent';

  // Validate create user form
  const canSubmitCreateUser = () => {
    if (!newUserEmail || !newUserName || !newUserCampusId || !newUserRole) return false;
    if (isStaffRole(newUserRole) && !newUserPhone) return false;
    if (isParentRole(newUserRole) && selectedStudents.length === 0) return false;
    return true;
  };

  // Manage linked students for a parent user
  const handleManageStudents = async (user) => {
    setSelectedUser(user);
    setShowLinkedStudentsModal(true);
    setLinkedStudentsLoading(true);
    try {
      const [linked, allStudents] = await Promise.all([
        getLinkedStudents(user.id),
        listStudents()
      ]);
      const userCampuses = user.campusIds || (user.campusId ? [user.campusId] : []);
      const campusStudents = allStudents.filter(s =>
        (s.campuses || []).some(c => userCampuses.includes(c))
      );
      // Available = currently linked to this parent + unassigned in campus
      const available = campusStudents.filter(s => !s.parentId || s.parentId === user.id);
      setLinkedStudentsAvailable(available);
      setLinkedStudentsSelected(linked.map(s => s.id));
    } catch (err) {
      console.error('Error loading linked students:', err);
      alert(`Failed to load students: ${err.message}`);
    } finally {
      setLinkedStudentsLoading(false);
    }
  };

  const handleSaveLinkedStudents = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await updateParentStudents(selectedUser.id, linkedStudentsSelected);
      setShowLinkedStudentsModal(false);
      setSelectedUser(null);
      alert('Student links updated successfully!\n学生关联更新成功！');
    } catch (err) {
      console.error('Error updating linked students:', err);
      alert(`Failed to update student links: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditCampus = (user) => {
    setSelectedUser(user);
    // Load user's current campus IDs (support both old and new format)
    const currentCampusIds = user.campusIds || (user.campusId ? [user.campusId] : []);
    setUserCampusIds(currentCampusIds);
    setCampusToAdd('');
    setTeachingCampusIds(user.teachingCampusIds || []);
    setTeachingCampusToAdd('');
    setShowCampusModal(true);
  };

  // Teaching campuses: add (any campus; confers no management authority)
  const handleAddTeachingCampus = async () => {
    if (!selectedUser || !teachingCampusToAdd) return;

    setActionLoading(true);
    try {
      const result = await addTeachingCampus(selectedUser.id, teachingCampusToAdd);
      const newTeaching = result.teachingCampusIds || [...teachingCampusIds, teachingCampusToAdd];
      setTeachingCampusIds(newTeaching);
      setUsers(users.map(u =>
        u.id === selectedUser.id ? { ...u, teachingCampusIds: newTeaching } : u
      ));
      setTeachingCampusToAdd('');
      alert('Teaching campus added successfully! / 授课校区添加成功！');
    } catch (err) {
      console.error('Error adding teaching campus:', err);
      alert(`Failed to add teaching campus: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Teaching campuses: remove
  const handleRemoveTeachingCampus = async (campusIdToRemove) => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      const result = await removeTeachingCampus(selectedUser.id, campusIdToRemove);
      const newTeaching = result.teachingCampusIds || teachingCampusIds.filter(c => c !== campusIdToRemove);
      setTeachingCampusIds(newTeaching);
      setUsers(users.map(u =>
        u.id === selectedUser.id ? { ...u, teachingCampusIds: newTeaching } : u
      ));
      alert('Teaching campus removed successfully! / 授课校区移除成功！');
    } catch (err) {
      console.error('Error removing teaching campus:', err);
      alert(`Failed to remove teaching campus: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Multi-campus: Add a campus to user
  const handleAddCampus = async () => {
    if (!selectedUser || !campusToAdd) return;

    setActionLoading(true);
    try {
      const result = await addUserToCampus(selectedUser.id, campusToAdd);
      const newCampusIds = result.campusIds || [...userCampusIds, campusToAdd];
      // Update local state
      setUserCampusIds(newCampusIds);
      setUsers(users.map(u =>
        u.id === selectedUser.id ? { ...u, campusIds: newCampusIds, campusId: newCampusIds[0] || '' } : u
      ));
      setCampusToAdd('');
      alert('Campus added successfully! / 校区添加成功！');
    } catch (err) {
      console.error('Error adding campus:', err);
      alert(`Failed to add campus: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Multi-campus: Remove a campus from user
  const handleRemoveCampus = async (campusIdToRemove) => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      const result = await removeUserFromCampus(selectedUser.id, campusIdToRemove);
      const newCampusIds = result.campusIds || userCampusIds.filter(c => c !== campusIdToRemove);
      // Update local state
      setUserCampusIds(newCampusIds);
      setUsers(users.map(u =>
        u.id === selectedUser.id ? { ...u, campusIds: newCampusIds, campusId: newCampusIds[0] || '' } : u
      ));
      alert('Campus removed successfully! / 校区移除成功！');
    } catch (err) {
      console.error('Error removing campus:', err);
      alert(`Failed to remove campus: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle disable user - opens confirmation modal
  const handleDisableUser = (user) => {
    setConfirmUser(user);
    setConfirmAction('disable');
    setShowConfirmModal(true);
  };

  // Handle enable user - direct action without confirmation
  const handleEnableUser = async (user) => {
    setActionLoading(true);
    try {
      await enableUser(user.id);
      setUsers(users.map(u =>
        u.id === user.id ? { ...u, status: 'active' } : u
      ));
      alert('User enabled successfully! / 用户已启用成功！');
    } catch (err) {
      console.error('Error enabling user:', err);
      alert(`Failed to enable user: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete user - opens confirmation modal
  const handleDeleteUser = (user) => {
    setConfirmUser(user);
    setConfirmAction('delete');
    setShowConfirmModal(true);
  };

  // Execute confirmed action (disable or delete)
  const executeConfirmedAction = async () => {
    if (!confirmUser || !confirmAction) return;

    setActionLoading(true);
    try {
      if (confirmAction === 'disable') {
        await disableUser(confirmUser.id);
        setUsers(users.map(u =>
          u.id === confirmUser.id ? { ...u, status: 'disabled' } : u
        ));
        alert('User disabled successfully! / 用户已禁用成功！');
      } else if (confirmAction === 'delete') {
        await deleteUser(confirmUser.id);
        // Remove from local state since deleted users are filtered in backend
        setUsers(users.filter(u => u.id !== confirmUser.id));
        alert('User deleted successfully! / 用户已删除成功！');
      }
    } catch (err) {
      console.error(`Error ${confirmAction}ing user:`, err);
      alert(`Failed to ${confirmAction} user: ${err.message}`);
    } finally {
      setActionLoading(false);
      setShowConfirmModal(false);
      setConfirmUser(null);
      setConfirmAction(null);
    }
  };

  // Get confirmation modal content based on action
  const getConfirmModalContent = () => {
    if (!confirmUser || !confirmAction) return { title: '', message: '', buttonText: '', buttonColor: '' };

    if (confirmAction === 'disable') {
      return {
        title: 'Disable User / 禁用用户',
        message: `Are you sure you want to disable "${confirmUser.name}"? They will not be able to log in until re-enabled.\n\n确定要禁用 "${confirmUser.name}" 吗？该用户将无法登录，直到重新启用。`,
        buttonText: 'Disable / 禁用',
        buttonColor: '#f59e0b'
      };
    } else if (confirmAction === 'delete') {
      return {
        title: 'Delete User / 删除用户',
        message: `Are you sure you want to delete "${confirmUser.name}"? This action cannot be undone and the user will be permanently removed from the system.\n\n确定要删除 "${confirmUser.name}" 吗？此操作无法撤销，该用户将被永久移除。`,
        buttonText: 'Delete / 删除',
        buttonColor: '#ef4444'
      };
    }
    return { title: '', message: '', buttonText: '', buttonColor: '' };
  };

  // Check if current user can edit a target user's profile
  const canEditUserProfile = (targetUser) => {
    if (!targetUser) return false;
    const targetRoles = targetUser.roles || [];
    const isAdmin = portalGroups.includes('portal_admin');

    // Never allow editing developer users
    if (targetRoles.includes('portal_developer')) return false;
    // Developer can edit anyone except developer
    if (isDeveloper) {
      return !targetRoles.includes('portal_developer');
    }
    // System Admin can edit anyone except system_admin/developer
    if (isSystemAdmin) {
      return !targetRoles.includes('portal_system_admin');
    }
    // Director can edit anyone except director/system_admin
    if (isDirector) {
      return !targetRoles.includes('portal_system_admin') && !targetRoles.includes('portal_director');
    }
    // Manager can only edit teacher/parent
    if (isManager) {
      const allowedRoles = ['portal_teacher', 'portal_parent'];
      return targetRoles.length === 0 || targetRoles.every(r => allowedRoles.includes(r));
    }
    // Admin can only edit teacher/parent
    if (isAdmin) {
      const allowedRoles = ['portal_teacher', 'portal_parent'];
      return targetRoles.length === 0 || targetRoles.every(r => allowedRoles.includes(r));
    }
    return false;
  };

  // Check if current user can edit a target user's roles
  const canEditUserRoles = (targetUser) => {
    if (!targetUser) return false;
    const targetRoles = targetUser.roles || [];

    // Never allow editing developer users' roles
    if (targetRoles.includes('portal_developer')) return false;
    // Developer can edit anyone's roles except developer
    if (isDeveloper) {
      return !targetRoles.includes('portal_developer');
    }
    // System Admin can edit anyone's roles except system_admin
    if (isSystemAdmin) {
      return !targetRoles.includes('portal_system_admin');
    }
    // Director can edit anyone except director/system_admin
    if (isDirector) {
      return !targetRoles.includes('portal_system_admin') && !targetRoles.includes('portal_director');
    }
    // Manager can edit admin, teacher, parent (not director/manager)
    if (isManager) {
      const restrictedRoles = ['portal_system_admin', 'portal_director', 'portal_manager'];
      return targetRoles.length === 0 || !targetRoles.some(r => restrictedRoles.includes(r));
    }
    // Admin can only edit teacher/parent/no-role users
    if (isAdmin) {
      const allowedRoles = ['portal_teacher', 'portal_parent'];
      return targetRoles.length === 0 || targetRoles.every(r => allowedRoles.includes(r));
    }
    return false;
  };

  const handleEditProfile = async (user) => {
    setSelectedUser(user);
    setProfileLoading(true);
    setShowProfileModal(true);

    try {
      const profile = await getUserProfile(user.id);
      setProfileForm({
        name: profile.name || '',
        phone: profile.phone || '',
        birthday: profile.birthday || '',
        address: profile.address || '',
        education: profile.education || '',
        wwcNumber: profile.wwcNumber || '',
        bio: profile.bio || '',
        hireDate: profile.hireDate || '',
        department: profile.department || '',
        position: profile.position || '',
        avatarUrl: profile.avatarUrl || '',
        teachableGrades: profile.teachableGrades || [],
        teachableSubjectIds: profile.teachableSubjectIds || []
      });
      // Load subjects for teaching preferences
      if ((user.roles || []).includes('portal_teacher')) {
        try {
          const subjectList = await listSubjectsCached();
          setProfileSubjects(subjectList);
        } catch (subErr) {
          console.warn('Could not load subjects:', subErr);
        }
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      // Use existing user data as fallback
      setProfileForm({
        name: user.name || '',
        phone: '',
        birthday: '',
        address: '',
        education: '',
        wwcNumber: '',
        bio: '',
        hireDate: '',
        department: '',
        position: '',
        avatarUrl: '',
        teachableGrades: [],
        teachableSubjectIds: []
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      await updateUserProfile(selectedUser.id, profileForm);
      // Update local user list with new name
      setUsers(users.map(u =>
        u.id === selectedUser.id ? { ...u, name: profileForm.name } : u
      ));
      setShowProfileModal(false);
      setSelectedUser(null);
      alert('User profile updated successfully!');
    } catch (err) {
      console.error('Error updating profile:', err);
      alert(`Failed to update profile: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleProfileAvatarUpload = async (file) => {
    if (!selectedUser) return;
    const newAvatarUrl = await uploadUserAvatar(selectedUser.id, file);
    setProfileForm({ ...profileForm, avatarUrl: newAvatarUrl });
    return newAvatarUrl;
  };

  // Count users by role
  const countByRole = (role) => {
    return users.filter(u => (u.roles || []).includes(role)).length;
  };

  // Show loading while checking auth
  if (auth.isLoading || loading) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>User Management</S.Title>
            <S.Subtitle>Loading...</S.Subtitle>
          </div>
        </S.Header>
      </S.Container>
    );
  }

  // Show permission denied if user cannot view users
  if (!canViewUsers && !loading) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>User Management</S.Title>
            <S.Subtitle>Access Restricted</S.Subtitle>
          </div>
        </S.Header>
        <S.ErrorBanner>
          You do not have permission to view users. Only authorised users can access this page.
        </S.ErrorBanner>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <S.Header>
        <div>
          <S.Title>User Management</S.Title>
          <S.Subtitle>Manage all platform users, roles, and permissions</S.Subtitle>
        </div>
        {canCreateUser && (
          <S.Button color="#F1AF59" onClick={() => setShowCreateModal(true)}>
            + Create New User
          </S.Button>
        )}
      </S.Header>

      {error && (
        <S.ErrorBanner>
          Error loading users: {error}. Showing demo data.
        </S.ErrorBanner>
      )}

      <S.FiltersBar>
        <S.SearchInput
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <S.FilterSelect value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
          <option value="all">All Roles</option>
          {VISIBLE_PORTAL_ROLES.map(role => (
            <option key={role} value={role}>{ROLE_DISPLAY_NAMES[role]}</option>
          ))}
        </S.FilterSelect>
        {canEditCampus && (
          <S.FilterSelect value={filterCampus} onChange={(e) => setFilterCampus(e.target.value)}>
            <option value="all">All Campuses</option>
            <option value="unassigned">Unassigned</option>
            {campuses.map(campus => (
              <option key={campus.id} value={campus.id}>{campus.name}</option>
            ))}
          </S.FilterSelect>
        )}
      </S.FiltersBar>

      <S.TableContainer>
        <S.Table $cards>
          <thead>
            <tr>
              <S.Th>Name</S.Th>
              <S.Th>Email</S.Th>
              <S.Th>Campus</S.Th>
              <S.Th>Roles</S.Th>
              <S.Th>Status</S.Th>
              <S.Th>Created</S.Th>
              <S.Th>Actions</S.Th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => {
              const isCurrentUser = user.subId === currentUserSub;
              return (
                <tr key={user.id}>
                  <S.Td data-label="Name">
                    <S.UserName>{user.name}</S.UserName>
                  </S.Td>
                  <S.Td data-label="Email">{user.email}</S.Td>
                  <S.Td data-label="Campus">
                    {(user.campusIds && user.campusIds.length > 0) || user.campusId ? (
                      <S.RoleBadges>
                        {(user.campusIds || (user.campusId ? [user.campusId] : [])).map(cId => (
                          <S.Badge key={cId} color="#6366f1" style={{ fontSize: '11px', padding: '2px 6px' }}>
                            {getCampusName(cId, campuses) || 'Unknown'}
                          </S.Badge>
                        ))}
                      </S.RoleBadges>
                    ) : (
                      <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Unassigned</span>
                    )}
                  </S.Td>
                  <S.Td data-label="Roles">
                    <S.RoleBadges>
                      {getVisibleRoles(user.roles).length > 0 ? (
                        getVisibleRoles(user.roles).map(role => (
                          <S.Badge key={role} color={getRoleColor(role)}>
                            {getRoleDisplayName(role)}
                          </S.Badge>
                        ))
                      ) : (
                        <S.Badge color="#6b7280">No Role</S.Badge>
                      )}
                    </S.RoleBadges>
                  </S.Td>
                  <S.Td data-label="Status">
                    <S.Badge color={
                      user.status === 'active' ? '#10b981' :
                      user.status === 'disabled' ? '#f59e0b' :
                      '#ef4444'
                    }>
                      {user.status === 'active' ? 'Active / 活跃' :
                       user.status === 'disabled' ? 'Disabled / 禁用' :
                       user.status}
                    </S.Badge>
                  </S.Td>
                  <S.Td data-label="Created">{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}</S.Td>
                  <S.Td data-label="Actions">
                    <S.ActionButtons>
                      {!isCurrentUser && canEditUserRoles(user) && (
                        <S.ActionButton color="#F1AF59" onClick={() => handleEditRoles(user)}>
                          Edit Roles
                        </S.ActionButton>
                      )}
                      {(user.roles || []).includes('portal_parent') && canEditUserRoles(user) && (
                        <S.ActionButton color="#8b5cf6" onClick={() => handleManageStudents(user)}>
                          Students
                        </S.ActionButton>
                      )}
                      {(canEditCampus || (isDirector && !isCurrentUser) || (!isDirector && (isManager || isAdmin) && !user.campusId && (!user.campusIds || user.campusIds.length === 0))) && (
                        <S.ActionButton color="#3b82f6" onClick={() => handleEditCampus(user)}>
                          Edit Campus
                        </S.ActionButton>
                      )}
                      {canEditUserProfile(user) && (
                        <S.ActionButton color="#10b981" onClick={() => handleEditProfile(user)}>
                          Edit Profile
                        </S.ActionButton>
                      )}
                      {canDisableUser && !isCurrentUser && (
                        <>
                          {user.status === 'active' ? (
                            <S.ActionButton color="#f59e0b" onClick={() => handleDisableUser(user)}>
                              Disable
                            </S.ActionButton>
                          ) : user.status === 'disabled' ? (
                            <S.ActionButton color="#10b981" onClick={() => handleEnableUser(user)}>
                              Enable
                            </S.ActionButton>
                          ) : null}
                        </>
                      )}
                      {canDeleteUser && !isCurrentUser && (
                        <S.ActionButton color="#ef4444" onClick={() => handleDeleteUser(user)}>
                          Delete
                        </S.ActionButton>
                      )}
                    </S.ActionButtons>
                  </S.Td>
                </tr>
              );
            })}
          </tbody>
        </S.Table>
      </S.TableContainer>

      <S.Stats>
        Total Users: {filteredUsers.length} |
        Directors: {countByRole('portal_director')} |
        Managers: {countByRole('portal_manager')} |
        Admins: {countByRole('portal_admin')} |
        Teachers: {countByRole('portal_teacher')} |
        Parents: {countByRole('portal_parent')}
      </S.Stats>

      {/* Create User Modal */}
      {showCreateModal && (
        <S.ModalOverlay>
          <S.Modal style={{ maxWidth: '600px' }}>
            <S.ModalHeader>
              <h3>Create New User / 创建新用户</h3>
              <S.CloseButton onClick={handleCloseCreateModal}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              {/* Basic Info */}
              <S.FormGroup>
                <label>Full Name / 姓名 *</label>
                <S.Input
                  type="text"
                  placeholder="Enter full name"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                />
              </S.FormGroup>
              <S.FormGroup>
                <label>Email Address / 邮箱 *</label>
                <S.Input
                  type="email"
                  placeholder="Enter email address"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                />
              </S.FormGroup>

              {/* Campus Selection (Required) */}
              <S.FormGroup>
                <label>Campus / 校区 *</label>
                <S.FilterSelect
                  value={newUserCampusId}
                  onChange={(e) => {
                    setNewUserCampusId(e.target.value);
                    setSelectedStudents([]); // Reset students when campus changes
                  }}
                >
                  <option value="">-- Select Campus / 选择校区 --</option>
                  {getAvailableCampuses().map(campus => (
                    <option key={campus.id} value={campus.id}>{campus.name}</option>
                  ))}
                </S.FilterSelect>
              </S.FormGroup>

              {/* Role Selection (Required, Single) */}
              <S.FormGroup>
                <label>Role / 角色 *</label>
                <S.FilterSelect
                  value={newUserRole}
                  onChange={(e) => {
                    setNewUserRole(e.target.value);
                    setNewUserPhone('');
                    setSelectedStudents([]);
                  }}
                >
                  <option value="">-- Select Role / 选择角色 --</option>
                  {getAssignableRoles().map(role => (
                    <option key={role} value={role}>{ROLE_DISPLAY_NAMES[role]}</option>
                  ))}
                </S.FilterSelect>
                <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  You can add additional roles later after the user is created.
                  <br />创建后可添加更多角色。
                </small>
              </S.FormGroup>

              {/* Phone Number (for Staff only) */}
              {isStaffRole(newUserRole) && (
                <S.FormGroup>
                  <label>Phone Number / 电话 *</label>
                  <S.Input
                    type="tel"
                    placeholder="e.g., 0412 345 678"
                    value={newUserPhone}
                    onChange={(e) => setNewUserPhone(e.target.value)}
                  />
                </S.FormGroup>
              )}

              {/* Student Selection (for Parent only) */}
              {isParentRole(newUserRole) && newUserCampusId && (
                <S.FormGroup>
                  <label>Link Students / 关联学生 *</label>
                  {loadingStudents ? (
                    <div style={{ padding: '10px', color: '#6b7280' }}>Loading students... / 加载学生中...</div>
                  ) : (
                    <StudentMultiSelect
                      students={availableStudents}
                      selectedIds={selectedStudents}
                      onSelectionChange={setSelectedStudents}
                      placeholder="Select one or more students / 选择一个或多个学生..."
                    />
                  )}
                  <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                    Select the student(s) this parent will be associated with.
                    <br />选择将与此家长关联的学生。
                  </small>
                </S.FormGroup>
              )}

              <S.Note>
                A temporary password will be sent to the user's email.
                <br /><br />
                用户将收到一封包含临时密码的邮件。
              </S.Note>
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={handleCloseCreateModal}>
                Cancel / 取消
              </S.Button>
              <S.Button
                color="#F1AF59"
                onClick={handleCreateUser}
                disabled={actionLoading || !canSubmitCreateUser()}
              >
                {actionLoading ? 'Creating... / 创建中...' : 'Create User / 创建用户'}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}

      {/* Edit Roles Modal */}
      {showEditModal && selectedUser && (
        <S.ModalOverlay>
          <S.Modal>
            <S.ModalHeader>
              <h3>Edit Roles for {selectedUser.name}</h3>
              <S.CloseButton onClick={() => setShowEditModal(false)}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              <S.RoleSelector>
                {getAssignableRoles().map(role => (
                  <S.RoleOption
                    key={role}
                    $selected={selectedRoles.includes(role)}
                    onClick={() => toggleRole(role)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(role)}
                      onChange={() => toggleRole(role)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#F1AF59' }}
                    />
                    <span>{ROLE_DISPLAY_NAMES[role]}</span>
                  </S.RoleOption>
                ))}
              </S.RoleSelector>
              <S.Note>
                Select one or more roles for this user. Users can have multiple roles.
                <br />
                为用户选择一个或多个角色。用户可以拥有多个角色。
              </S.Note>

              {/* Student selection when adding parent role */}
              {selectedRoles.includes('portal_parent') &&
               !(selectedUser?.roles || []).includes('portal_parent') && (
                <S.FormGroup style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <label>Link Students / 关联学生 *</label>
                  {editRoleLoadingStudents ? (
                    <div style={{ padding: '10px', color: '#6b7280' }}>Loading students... / 加载学生中...</div>
                  ) : (selectedUser?.campusIds?.length || selectedUser?.campusId) ? (
                    editRoleAvailableStudents.length > 0 ? (
                      <StudentMultiSelect
                        students={editRoleAvailableStudents}
                        selectedIds={editRoleStudents}
                        onSelectionChange={setEditRoleStudents}
                        placeholder="Select one or more students / 选择一个或多个学生..."
                      />
                    ) : (
                      <div style={{ padding: '10px', color: '#ef4444', fontSize: '13px' }}>
                        No unassigned students found in this user's campus.
                        <br />该用户校区内没有可分配的学生。
                      </div>
                    )
                  ) : (
                    <div style={{ padding: '10px', color: '#ef4444', fontSize: '13px' }}>
                      User must have a campus assigned before adding parent role.
                      <br />用户必须先分配校区才能添加家长角色。
                    </div>
                  )}
                </S.FormGroup>
              )}

              {/* Warning when removing parent role */}
              {!selectedRoles.includes('portal_parent') &&
               (selectedUser?.roles || []).includes('portal_parent') && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#991b1b',
                  fontSize: '13px'
                }}>
                  Warning: Removing parent role will unlink all associated students from this parent.
                  <br />警告：移除家长角色将解除该家长与所有关联学生的绑定。
                </div>
              )}
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={() => setShowEditModal(false)}>
                Cancel
              </S.Button>
              <S.Button
                color="#F1AF59"
                onClick={handleSaveRoles}
                disabled={actionLoading || (selectedRoles.includes('portal_parent') && !(selectedUser?.roles || []).includes('portal_parent') && editRoleStudents.length === 0)}
              >
                {actionLoading ? 'Saving...' : 'Save Roles'}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}

      {/* Edit Campus Modal - Multi-Campus Support */}
      {showCampusModal && selectedUser && (
        <S.ModalOverlay>
          <S.Modal style={{ maxWidth: '500px' }}>
            <S.ModalHeader>
              <h3>Edit Campuses for {selectedUser.name}</h3>
              <S.CloseButton onClick={() => setShowCampusModal(false)}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              {/* Current Campuses List */}
              <S.FormGroup>
                <label>Current Campuses / 当前校区</label>
                {userCampusIds.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    {userCampusIds.map(cId => {
                      const campusName = getCampusName(cId, campuses) || 'Unknown';
                      // Check if current user can remove this campus
                      // System Admin/Developer/Director can remove any campus; Manager/Admin can only remove their own
                      const canRemove = canEditCampus || isDirector || myOwnCampusIds.includes(cId);
                      return (
                        <div
                          key={cId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: '#e0e7ff',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}
                        >
                          <span>{campusName}</span>
                          {canRemove && (
                            <button
                              onClick={() => handleRemoveCampus(cId)}
                              disabled={actionLoading}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#ef4444',
                                cursor: 'pointer',
                                fontSize: '16px',
                                padding: '0 2px',
                                lineHeight: 1
                              }}
                              title="Remove campus / 移除校区"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ color: '#9ca3af', fontStyle: 'italic', marginTop: '8px' }}>
                    No campus assigned / 未分配校区
                  </div>
                )}
              </S.FormGroup>

              {/* Add Campus - Director restricted to own campuses; System Admin/Developer can add any */}
              <S.FormGroup style={{ marginTop: '20px' }}>
                <label>Add Campus / 添加校区</label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <S.FilterSelect
                    value={campusToAdd}
                    onChange={(e) => setCampusToAdd(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="">-- Select Campus / 选择校区 --</option>
                    {campuses.filter(c => {
                      if (userCampusIds.includes(c.id)) return false;
                      if (!canEditCampus && (isDirector || isManager || isAdmin)) {
                        return myOwnCampusIds.includes(c.id);
                      }
                      return true;
                    }).map(campus => (
                      <option key={campus.id} value={campus.id}>{campus.name}</option>
                    ))}
                  </S.FilterSelect>
                  <S.Button
                    color="#10b981"
                    onClick={handleAddCampus}
                    disabled={actionLoading || !campusToAdd}
                    style={{ padding: '8px 16px' }}
                  >
                    {actionLoading ? '...' : '+ Add'}
                  </S.Button>
                </div>
              </S.FormGroup>

              <S.Note style={{ marginTop: '16px' }}>
                {canEditCampus
                  ? 'You can add or remove any campus. Users can belong to multiple campuses.\n您可以添加或移除任何校区。用户可以属于多个校区。'
                  : isDirector
                    ? 'You can add campuses you manage and remove any campus. Users can belong to multiple campuses.\n您可以添加您管理的校区并移除任何校区。用户可以属于多个校区。'
                    : 'You can add your own campus (for transfer). You can only remove your own campus.\n您可以添加自己的校区（用于调配）。您只能移除自己的校区。'}
              </S.Note>

              {/* Teaching Campuses — only for teachers. Separate from management campuses:
                  a teaching campus lets the teacher be assigned at that campus but grants
                  NO admin/manager authority there. Any campus may be added. */}
              {(selectedUser.roles || []).includes('portal_teacher') && (
                <>
                  <div style={{ borderTop: '1px solid #e5e7eb', margin: '20px 0 0' }} />
                  <S.FormGroup style={{ marginTop: '20px' }}>
                    <label>Teaching Campuses / 授课校区</label>
                    {teachingCampusIds.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                        {teachingCampusIds.map(cId => {
                          const campusName = getCampusName(cId, campuses) || 'Unknown';
                          return (
                            <div
                              key={cId}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: '#d1fae5',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                fontSize: '13px'
                              }}
                            >
                              <span>{campusName}</span>
                              <button
                                onClick={() => handleRemoveTeachingCampus(cId)}
                                disabled={actionLoading}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  padding: '0 2px',
                                  lineHeight: 1
                                }}
                                title="Remove teaching campus / 移除授课校区"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ color: '#9ca3af', fontStyle: 'italic', marginTop: '8px' }}>
                        No teaching campus / 无授课校区
                      </div>
                    )}
                  </S.FormGroup>

                  <S.FormGroup style={{ marginTop: '16px' }}>
                    <label>Add Teaching Campus / 添加授课校区</label>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <S.FilterSelect
                        value={teachingCampusToAdd}
                        onChange={(e) => setTeachingCampusToAdd(e.target.value)}
                        style={{ flex: 1 }}
                      >
                        <option value="">-- Select Campus / 选择校区 --</option>
                        {campuses.filter(c => !teachingCampusIds.includes(c.id)).map(campus => (
                          <option key={campus.id} value={campus.id}>{campus.name}</option>
                        ))}
                      </S.FilterSelect>
                      <S.Button
                        color="#10b981"
                        onClick={handleAddTeachingCampus}
                        disabled={actionLoading || !teachingCampusToAdd}
                        style={{ padding: '8px 16px' }}
                      >
                        {actionLoading ? '...' : '+ Add'}
                      </S.Button>
                    </div>
                  </S.FormGroup>

                  <S.Note style={{ marginTop: '16px' }}>
                    {'Teaching campuses let this teacher be assigned to course groups at those campuses. They grant NO management access — only the management campus above does.\n授课校区允许该教师被分配到这些校区的课程组授课，但不授予任何管理权限——管理权限只由上方的管理校区决定。'}
                  </S.Note>
                </>
              )}
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={() => setShowCampusModal(false)}>
                Close / 关闭
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}

      {/* Edit Profile Modal */}
      {showProfileModal && selectedUser && (
        <S.ModalOverlay>
          <S.Modal style={{ maxWidth: '600px' }}>
            <S.ModalHeader>
              <h3>Edit Profile: {selectedUser.name}</h3>
              <S.CloseButton onClick={() => setShowProfileModal(false)}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              {profileLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>Loading profile...</div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                    <AvatarUpload
                      avatarUrl={profileForm.avatarUrl}
                      name={profileForm.name}
                      size={80}
                      editable={true}
                      onUpload={(url) => setProfileForm({ ...profileForm, avatarUrl: url })}
                      uploadFunction={handleProfileAvatarUpload}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <S.FormGroup>
                      <label>Full Name</label>
                      <S.Input
                        type="text"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      />
                    </S.FormGroup>
                    <S.FormGroup>
                      <label>Phone</label>
                      <S.Input
                        type="text"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                        placeholder="e.g., 0412 345 678"
                      />
                    </S.FormGroup>
                    <S.FormGroup>
                      <label>Date of Birth</label>
                      <S.Input
                        type="date"
                        value={profileForm.birthday}
                        onChange={(e) => setProfileForm({ ...profileForm, birthday: e.target.value })}
                      />
                    </S.FormGroup>
                    <S.FormGroup>
                      <label>Hire Date</label>
                      <S.Input
                        type="date"
                        value={profileForm.hireDate}
                        onChange={(e) => setProfileForm({ ...profileForm, hireDate: e.target.value })}
                      />
                    </S.FormGroup>
                    <S.FormGroup>
                      <label>Department</label>
                      <S.Input
                        type="text"
                        value={profileForm.department}
                        onChange={(e) => setProfileForm({ ...profileForm, department: e.target.value })}
                      />
                    </S.FormGroup>
                    <S.FormGroup>
                      <label>Position</label>
                      <S.Input
                        type="text"
                        value={profileForm.position}
                        onChange={(e) => setProfileForm({ ...profileForm, position: e.target.value })}
                      />
                    </S.FormGroup>
                    <S.FormGroup style={{ gridColumn: 'span 2' }}>
                      <label>Address</label>
                      <S.Input
                        type="text"
                        value={profileForm.address}
                        onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                      />
                    </S.FormGroup>
                    <S.FormGroup>
                      <label>Education</label>
                      <S.Input
                        type="text"
                        value={profileForm.education}
                        onChange={(e) => setProfileForm({ ...profileForm, education: e.target.value })}
                      />
                    </S.FormGroup>
                    <S.FormGroup>
                      <label>WWC Number</label>
                      <S.Input
                        type="text"
                        value={profileForm.wwcNumber}
                        onChange={(e) => setProfileForm({ ...profileForm, wwcNumber: e.target.value })}
                      />
                    </S.FormGroup>
                    <S.FormGroup style={{ gridColumn: 'span 2' }}>
                      <label>Bio</label>
                      <textarea
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          minHeight: '80px',
                          resize: 'vertical'
                        }}
                        value={profileForm.bio}
                        onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                        placeholder="Brief bio..."
                      />
                    </S.FormGroup>
                  </div>

                  {selectedUser && (selectedUser.roles || []).includes('portal_teacher') && (
                    <div style={{ marginTop: '20px' }}>
                      <div style={{ fontSize: '15px', fontWeight: '600', color: '#374151', marginBottom: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                        Teaching Preferences
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>Teachable Grades</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                        {YEAR_OPTIONS.map(opt => (
                          <div
                            key={opt.value}
                            onClick={() => {
                              const grades = profileForm.teachableGrades.includes(opt.value)
                                ? profileForm.teachableGrades.filter(g => g !== opt.value)
                                : [...profileForm.teachableGrades, opt.value];
                              // Unticking the last of Year 11/12 takes the ATAR subjects with it
                              setProfileForm({
                                ...profileForm,
                                teachableGrades: grades,
                                teachableSubjectIds: keepAtarSubjectsForGrades(profileForm.teachableSubjectIds, grades)
                              });
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              border: `2px solid ${profileForm.teachableGrades.includes(opt.value) ? '#F1AF59' : '#e5e7eb'}`,
                              background: profileForm.teachableGrades.includes(opt.value) ? '#fef3e2' : '#fff',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              userSelect: 'none'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={profileForm.teachableGrades.includes(opt.value)}
                              onChange={() => {}}
                              style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#F1AF59' }}
                            />
                            <span>{opt.label}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>Teachable Subjects</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {[
                          ...profileSubjects.map(s => ({ id: s.id, label: s.subjectName })),
                          ...(teachesAtarYears(profileForm.teachableGrades) ? ATAR_SUBJECTS : [])
                        ].map(subject => (
                          <div
                            key={subject.id}
                            onClick={() => {
                              const subjects = profileForm.teachableSubjectIds.includes(subject.id)
                                ? profileForm.teachableSubjectIds.filter(s => s !== subject.id)
                                : [...profileForm.teachableSubjectIds, subject.id];
                              setProfileForm({ ...profileForm, teachableSubjectIds: subjects });
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              border: `2px solid ${profileForm.teachableSubjectIds.includes(subject.id) ? '#F1AF59' : '#e5e7eb'}`,
                              background: profileForm.teachableSubjectIds.includes(subject.id) ? '#fef3e2' : '#fff',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              userSelect: 'none'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={profileForm.teachableSubjectIds.includes(subject.id)}
                              onChange={() => {}}
                              style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#F1AF59' }}
                            />
                            <span>{subject.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={() => setShowProfileModal(false)}>
                Cancel
              </S.Button>
              <S.Button
                color="#10b981"
                onClick={handleSaveProfile}
                disabled={actionLoading || profileLoading}
              >
                {actionLoading ? 'Saving...' : 'Save Profile'}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}

      {/* Confirmation Modal for Disable/Delete */}
      {showConfirmModal && confirmUser && (
        <S.ModalOverlay>
          <S.Modal style={{ maxWidth: '450px' }}>
            <S.ModalHeader>
              <h3>{getConfirmModalContent().title}</h3>
              <S.CloseButton onClick={() => {
                setShowConfirmModal(false);
                setConfirmUser(null);
                setConfirmAction(null);
              }}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              <div style={{ textAlign: 'center', fontSize: '48px', marginBottom: '16px' }}>
                {confirmAction === 'delete' ? '⚠️' : '⚡'}
              </div>
              <div style={{
                fontSize: '14px',
                color: '#374151',
                lineHeight: '1.6',
                textAlign: 'center',
                whiteSpace: 'pre-line',
                marginBottom: '16px'
              }}>
                {getConfirmModalContent().message}
              </div>
              <div style={{
                background: '#f3f4f6',
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#4b5563',
                lineHeight: '1.6'
              }}>
                <strong>User / 用户:</strong> {confirmUser.name}<br />
                <strong>Email / 邮箱:</strong> {confirmUser.email}
              </div>
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button
                color="#6b7280"
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmUser(null);
                  setConfirmAction(null);
                }}
              >
                Cancel / 取消
              </S.Button>
              <S.Button
                color={getConfirmModalContent().buttonColor}
                onClick={executeConfirmedAction}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : getConfirmModalContent().buttonText}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}

      {/* Linked Students Management Modal */}
      {showLinkedStudentsModal && selectedUser && (
        <S.ModalOverlay>
          <S.Modal style={{ maxWidth: '600px' }}>
            <S.ModalHeader>
              <h3>Manage Linked Students / 管理关联学生</h3>
              <S.CloseButton onClick={() => { setShowLinkedStudentsModal(false); setSelectedUser(null); }}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              <div style={{ marginBottom: '16px', padding: '12px', background: '#f3f4f6', borderRadius: '8px', fontSize: '13px' }}>
                <strong>Parent / 家长:</strong> {selectedUser.name} ({selectedUser.email})
              </div>
              {linkedStudentsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading students... / 加载学生中...</div>
              ) : (
                <>
                  <S.FormGroup>
                    <label>Linked Students / 关联学生</label>
                    {linkedStudentsAvailable.length > 0 ? (
                      <StudentMultiSelect
                        students={linkedStudentsAvailable}
                        selectedIds={linkedStudentsSelected}
                        onSelectionChange={setLinkedStudentsSelected}
                        placeholder="Select students to link / 选择要关联的学生..."
                      />
                    ) : (
                      <div style={{ padding: '10px', color: '#6b7280', fontStyle: 'italic' }}>
                        No students available in this parent's campus.
                        <br />该家长校区内没有可分配的学生。
                      </div>
                    )}
                  </S.FormGroup>
                  <S.Note>
                    Select or deselect students to update the parent-student links. Only unassigned students from the parent's campus are shown.
                    <br />选择或取消选择学生以更新家长-学生关联。仅显示家长校区内未分配的学生。
                  </S.Note>
                </>
              )}
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={() => { setShowLinkedStudentsModal(false); setSelectedUser(null); }}>
                Cancel / 取消
              </S.Button>
              <S.Button
                color="#F1AF59"
                onClick={handleSaveLinkedStudents}
                disabled={actionLoading || linkedStudentsLoading}
              >
                {actionLoading ? 'Saving...' : 'Save / 保存'}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}
    </S.Container>
  );
};

export default UserManagement;
